import { motion } from "framer-motion";
import { Battery, Wind, Cable, Zap, Gauge, ShieldCheck } from "lucide-react";
import CornerMarks from "@/components/site/CornerMarks";

const ITEMS = [
  { icon: Battery, label: "Grid-Tie / Hybrid / Off-Grid", spec: "3 kW – 1 MW" },
  { icon: Wind, label: "HVAC & Ventilation", spec: "Design · Commissioning" },
  { icon: Cable, label: "LV / MV Power Distribution", spec: "415 V – 11 kV" },
  { icon: Zap, label: "Battery Backup & UPS", spec: "Li-Ion · LFP" },
  { icon: Gauge, label: "Energy Monitoring & Audits", spec: "Audit · Reports" },
  { icon: ShieldCheck, label: "Earthing, LPS & Compliance", spec: "Surge · Lightning" },
];

const Capabilities = () => {
  return (
    <section
      id="capabilities"
      className="relative py-16 lg:py-24 overflow-hidden"
      style={{ backgroundColor: "var(--bg-soft)" }}
      data-testid="capabilities-section"
    >
      <div className="absolute inset-0 energy-grid-bg opacity-50 pointer-events-none" />
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
          opacity: 0.5,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <span className="eyebrow">Capabilities · live at launch</span>
            <h2
              className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight"
              style={{ color: "var(--text)" }}
            >
              Full-stack building engineering.
            </h2>
          </div>
          <p
            className="max-w-md text-sm leading-relaxed"
            style={{ color: "var(--text-muted)" }}
          >
            From single-line diagrams to commissioning sign-off, our scope spans
            every discipline a modern building demands.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px"
             style={{ background: "var(--border)" }}>
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group relative p-5 lg:p-6 transition-colors duration-300"
                style={{ background: "var(--bg)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--surface)")
                }
                onMouseLeave={(e) => (e.currentTarget.style.background = "var(--bg)")}
                data-testid={`capability-${i}`}
              >
                <Icon
                  className="w-5 h-5"
                  strokeWidth={1.5}
                  style={{ color: "var(--accent-blue)" }}
                />
                <p
                  className="mt-4 font-display text-[13px] font-medium leading-snug"
                  style={{ color: "var(--text)" }}
                >
                  {it.label}
                </p>
                <p
                  className="mt-2 font-mono-spec text-[10px] tracking-[0.2em] uppercase"
                  style={{ color: "var(--text-faint)" }}
                >
                  {it.spec}
                </p>
                <span
                  className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-700"
                  style={{ background: "var(--accent-blue)" }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
      <CornerMarks code="SEC—01.CAPABILITIES" />
    </section>
  );
};

export default Capabilities;
